
import { defineComponent, ref, computed } from "vue";
import { useRouterCustom } from "@/core/hooks/use-router-custom";
import {
  IDanhMucToaXeResponseBase,
  IToaXeResponseBase,
} from "@/core/interfaces/ApiResponses";
import {
  setBreadcrumbLinks,
  setCurrentPageTitle,
} from "@/core/helpers/breadcrumb";
import { useDateTime } from "@/core/hooks/use-date-time-hook";
import {
  getErrorMsg,
  swalErrNotification,
  swalNotification,
} from "@/core/helpers/utils";
import { useButtonCustom } from "@/core/hooks/use-button-custom";
import { useLoading } from "@/core/hooks/use-loading";

import DanhMucToaXeService from "@/core/services/DanhMucToaXe.service";
import DanhSachToaXe from "@/views/crafted/pages/danh-muc-toa-xe/danh-sach-toa-xe-cua-danh-muc/DanhSachToaXe.vue";
import ModalThemToaXe from "@/views/crafted/pages/danh-muc-toa-xe/modal-them-toa-xe/ModalThemToaXe.vue";
import DinhMucCoDinhToaXeSection from "@/views/crafted/pages/danh-muc-toa-xe/dinh-muc-co-dinh-section/DinhMucCoDinhToaXeSection.vue";
import PageContentBase from "@/layout/_shared/PageContentBase.vue";
import PageContentSection from "@/layout/_shared/PageContentSection.vue";
import ButtonCustom from "@/components/shared/buttons/ButtonCustom.vue";
import Can from "@/components/Can.vue";
import { DANH_MUC_TOA_XE } from "@/core/constants/permissions";
import ConfirmationDialog from "@/components/confirmation-dialog/ConfirmationDialog.vue";
import DetailTopicLottie from "@/components/shared/detail-topic-lottie/DetailTopicLottie.vue";

export default defineComponent({
  name: "chi-tiet-danh-muc-toa-xe",

  components: {
    ButtonCustom,
    Can,
    ConfirmationDialog,
    DetailTopicLottie,
    DanhSachToaXe,
    DinhMucCoDinhToaXeSection,
    ModalThemToaXe,
    PageContentBase,
    PageContentSection,
  },

  data() {
    return {
      isModalOpen: false,
      isDeletingModalDisplay: false,
    };
  },

  setup() {
    const { goBack, push, replace, id } = useRouterCustom();

    const { moment } = useDateTime();

    const { ButtonTypeColors, ButtonsType } = useButtonCustom();

    const danhSachToaXeRef = ref();

    const danhMucToaXe = ref<IDanhMucToaXeResponseBase | null>(null);

    const removingToaXe = ref<IToaXeResponseBase | null>(null);

    const { startLoading, endLoading } = useLoading();

    const isPhanCapCuoiCung = computed(() => {
      if (danhMucToaXe.value) {
        return danhMucToaXe.value.phan_cap === 1;
      }

      return false;
    });

    return {
      DANH_MUC_TOA_XE,
      danhMucToaXeId: id,
      danhSachToaXeRef,
      isPhanCapCuoiCung,
      danhMucToaXe,
      goBack,
      push,
      replace,
      moment,
      ButtonTypeColors,
      ButtonsType,
      removingToaXe,
      startLoading,
      endLoading,
    };
  },

  async mounted() {
    setBreadcrumbLinks([
      {
        text: "Nhóm toa xe",
        routeData: {
          name: "danh-muc-toa-xe",
        },
      },
      {
        text: "Chi tiết",
      },
    ]);

    await this.fetchData();
    document.title = `${this.danhMucToaXe?.ten_danh_muc} (${this.danhMucToaXe?.ma_danh_muc}) - Nhóm toa xe - Đường sắt Hà Nội`;

    setCurrentPageTitle(
      `${this.danhMucToaXe?.ten_danh_muc} (${this.danhMucToaXe?.ma_danh_muc})` ||
        ""
    );
  },

  methods: {
    async fetchData() {
      try {
        const {
          data: { data: res },
        } = await DanhMucToaXeService.get(this.danhMucToaXeId);
        this.danhMucToaXe = res;
      } catch (err) {
        await swalNotification(
          "Có lỗi xảy ra khi lấy thông tin nhóm toa xe",
          "error"
        );
      }
    },

    openModal() {
      this.isModalOpen = true;
    },

    async onAddedToaXeItems() {
      this.isModalOpen = false;

      await this.fetchData();

      this.danhSachToaXeRef.currentPage = 1;

      await this.danhSachToaXeRef.fetchToaXeItems();

      this.endLoading();
    },

    async confirmDelete() {
      this.isDeletingModalDisplay = false;
      try {
        await DanhMucToaXeService.delete(this.danhMucToaXeId);
        await swalNotification("Xóa thành công", "success");
        await this.replace("/quan-ly/danh-muc-toa-xe/danh-sach");
      } catch (error) {
        await swalNotification(
          getErrorMsg(error, "Có lỗi xảy ra, không thể xóa nhóm toa xe này"),
          "error"
        );
      }
    },

    async confirmRemoveVatTu() {
      try {
        this.startLoading();

        await DanhMucToaXeService.detachToaXe({
          id_toa_xe: this.removingToaXe.id,
          id_dm_toa_xe: this.danhMucToaXeId,
        });

        this.removingToaXe = null;

        this.endLoading();

        await swalNotification("Xóa thành công", "success");

        await this.fetchData();

        await this.danhSachToaXeRef.fetchToaXeItems();
      } catch (e) {
        this.endLoading();

        this.removingToaXe = null;

        swalErrNotification(e, "Có lỗi xảy ra khi xóa toa xe khỏi nhóm toa xe");
      }
    },
  },
});
